import { useContext, useEffect, useRef, useState } from 'react';
import moment from 'moment';

import { DeletedStock, StocksContext } from '../../context/stock/StockContext';

interface DeletedStockNoticeProps {
    code: string;
    reactivate: boolean;
    onReactivateChange: (reactivate: boolean) => void;
    // Informa al padre si hay un producto eliminado con ese código (para ajustar textos)
    onMatchChange?: (match: DeletedStock | null) => void;
    disabled?: boolean;
}

// Aviso al crear un producto de inventario con el código de uno que fue eliminado:
// por defecto se reactiva (conserva ID e historial), o se puede crear como nuevo.
export const DeletedStockNotice = ({ code, reactivate, onReactivateChange, onMatchChange, disabled }: DeletedStockNoticeProps) => {
    const { findDeletedStockByCode } = useContext(StocksContext);
    const [match, setMatch] = useState<DeletedStock | null>(null);
    const requestRef = useRef(0);

    useEffect(() => {
        const value = code.trim();
        const requestId = ++requestRef.current;

        if (!value) {
            setMatch(null);
            onMatchChange?.(null);
            return;
        }

        const timer = setTimeout(async () => {
            const found = await findDeletedStockByCode(value);
            if (requestId !== requestRef.current) return;
            setMatch(found);
            onMatchChange?.(found);
            if (found) onReactivateChange(true);
        }, 400);

        return () => clearTimeout(timer);
    }, [code]);

    if (!match) return null;

    const deletedDate = match.deletedAt ? moment(match.deletedAt).format('DD/MM/YYYY') : null;

    return (
        <div className="alert alert-info border-0 mb-0">
            <div className="d-flex gap-2">
                <i className="bi bi-arrow-counterclockwise mt-1" />
                <div className="small">
                    <div className="fw-semibold mb-1">Este código pertenece a un producto eliminado</div>
                    <div className="mb-2">
                        <span className="fw-medium">{match.name}</span> ({match.code})
                        {deletedDate ? <> se eliminó del inventario el {deletedDate}.</> : <> fue eliminado del inventario.</>}
                    </div>

                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="radio"
                            id="stockReactivate"
                            checked={reactivate}
                            disabled={disabled}
                            onChange={() => onReactivateChange(true)}
                        />
                        <label className="form-check-label" htmlFor="stockReactivate">
                            <span className="fw-medium">Reactivarlo</span>
                            <span className="text-muted"> — conserva su historial de movimientos; la cantidad se registra como una entrada.</span>
                        </label>
                    </div>
                    <div className="form-check">
                        <input
                            className="form-check-input"
                            type="radio"
                            id="stockCreateNew"
                            checked={!reactivate}
                            disabled={disabled}
                            onChange={() => onReactivateChange(false)}
                        />
                        <label className="form-check-label" htmlFor="stockCreateNew">
                            <span className="fw-medium">Crear un producto nuevo</span>
                            <span className="text-muted"> — empieza sin historial; el eliminado queda como está.</span>
                        </label>
                    </div>
                </div>
            </div>
        </div>
    );
};
